'use strict';

// Pure helpers for org accounts. See docs/org-accounts-spec.md §4.

// An org account with type 'admin' must not confer the admin bypass
// (lib/api.js) on its members, so `type` is deliberately not propagated.
function buildOrgTokenPayload(orgPerson, actorKey) {
    return {
        personKey: orgPerson.key,
        actorKey: actorKey,
        provider: 'org-switch',
        scope: 'rw'
    };
}

// Seats count members only; the owner is not a seat.
// The ladder lives in tier.seats (NULL = unlimited) and is loaded at boot by
// lib/stripeapi.js loadTiers(), so this map is populated server-side only. The
// client gets its own cap as `seats` on the profile and meter responses,
// already passed through wireSeats().
var ORG_SEATS = {};

var DEFAULT_ORG_SEATS = 1;

function loadSeatMap(tiers) {
    ORG_SEATS = {};
    (tiers || []).forEach(function (tier) {
        ORG_SEATS[tier.id] = tier.seats == null ? Infinity : Number(tier.seats);
    });
}

// JSON can't carry Infinity, so an unlimited cap travels as 0 (no tier has
// zero seats). wireSeats and seatCap are the two directions of that encoding
// and must stay in step: the server sends wireSeats(cap), the client reads
// seatCap(seats). A missing value gets the free allowance, never Unlimited.
function wireSeats(cap) {
    return cap === Infinity ? 0 : cap;
}

function seatCap(seats) {
    if (seats === null || seats === undefined || seats === '') return DEFAULT_ORG_SEATS;
    var n = Number(seats);
    if (isNaN(n)) return DEFAULT_ORG_SEATS;
    return n === 0 ? Infinity : n;
}

// Seat index of a membership: how many active memberships are older. Seats go
// by seniority, so a downgrade suspends the newest rather than deleting rows.
// Shared by lib/orgAPI.js and resolveOrgActor below; the JS equivalent is
// seatSuspensionFlags() below and the two must stay in step.
var SEAT_INDEX_SQL =
    '(SELECT count(*) FROM org_member o2 ' +
    '  WHERE o2."orgKey" = om."orgKey" AND o2.status = \'active\' ' +
    '    AND (o2."createdAt" < om."createdAt" OR (o2."createdAt" = om."createdAt" AND o2.id < om.id))' +
    ') AS seat_index';

function seatsForTier(tierId) {
    //an unknown or missing tier gets the free allowance, never Unlimited
    if (tierId === null || tierId === undefined || tierId === '') return DEFAULT_ORG_SEATS;
    var seats = ORG_SEATS[Number(tierId)];
    return seats === undefined ? DEFAULT_ORG_SEATS : seats;
}

function seatIsWithinCap(seatIndex, tierId) {
    return Number(seatIndex) < seatsForTier(tierId);
}

var ACTIVE_MEMBER_SQL =
    'SELECT p.tier_id, om.developer_access, ' + SEAT_INDEX_SQL + ' ' +
    'FROM org_member om JOIN person p ON p.key = om."orgKey" ' +
    'WHERE om."orgKey" = :orgKey AND om."personKey" = :actorKey AND om.status = \'active\'';

// Re-checks an org-context token (personKey = org, actorKey = member) against
// the live membership and seat on every request, so revocation and downgrades
// take effect at once (spec §4.2). Every auth strategy that accepts app JWTs
// goes through here. Resolves to the credentials to authenticate with, or null
// to reject. sequelize is passed in to keep this module free of requires.
function resolveOrgActor(sequelize, credentials) {
    return sequelize.query(ACTIVE_MEMBER_SQL, {
        replacements: {orgKey: credentials.personKey, actorKey: credentials.actorKey},
        type: sequelize.QueryTypes.SELECT
    }).then(function (rows) {
        if (!rows || rows.length == 0) return null;
        if (!seatIsWithinCap(rows[0].seat_index, rows[0].tier_id)) return null;
        //grants ride on credentials so the guard sees them per request
        return Object.assign({}, credentials, {developerAccess: rows[0].developer_access === true});
    });
}

// Suspension flags for a seniority-ordered list. Only active memberships
// consume a seat.
function seatSuspensionFlags(rows, tierId) {
    var activeSeen = 0;
    return (rows || []).map(function (r) {
        if (!r || r.status !== 'active') return false;
        var suspended = !seatIsWithinCap(activeSeen, tierId);
        activeSeen++;
        return suspended;
    });
}

// Seat display for the ORGANIZATION section, built from the wire-encoded
// `seats` the server sends on the profile. `used` counts members holding or
// invited to a seat; the server is authoritative, this drives the UI.
function seatSummary(seats, used) {
    var cap = seatCap(seats);
    var u = Number(used) > 0 ? Number(used) : 0;
    return {cap: cap, used: u, remaining: cap === Infinity ? Infinity : Math.max(0, cap - u)};
}

// A search box over a handful of members is noise.
var MEMBER_SEARCH_THRESHOLD = 10;

function showMemberSearch(memberCount) {
    return Number(memberCount) > MEMBER_SEARCH_THRESHOLD;
}

// Reads the payload as submitted, before the handler swaps 'rotate' markers for
// generated values, and never reports a secret itself.
function describeDeveloperChanges(payload) {
    if (!payload) return [];
    var changes = [];
    if (payload.api_key === 'rotate') changes.push('API key rotated');
    if (payload.oauth_client_secret === 'rotate') changes.push('OAuth client secret rotated');
    if (payload.webhooks_secret === 'rotate') changes.push('webhook signing secret rotated');
    if (payload.oauth_service_name !== undefined) changes.push('OAuth service name changed');
    if (payload.oauth_redirect_uri !== undefined || payload.oauth_redirect_uris !== undefined) changes.push('OAuth redirect URIs changed');
    if (payload.webhooks !== undefined) changes.push('webhooks updated');
    return changes;
}

function orgDisplayName(person) {
    if (!person) return '';
    return person.org_name || person.name || '';
}

// invited -> active|declined|revoked, active -> revoked,
// declined|revoked -> invited (re-invite). Nothing else.
function validTransition(from, to) {
    if (from === 'invited' && (to === 'active' || to === 'declined' || to === 'revoked')) return true;
    if (from === 'active' && to === 'revoked') return true;
    if ((from === 'declined' || from === 'revoked') && to === 'invited') return true;
    return false;
}

module.exports = {
    SEAT_INDEX_SQL: SEAT_INDEX_SQL,
    loadSeatMap: loadSeatMap,
    wireSeats: wireSeats,
    seatCap: seatCap,
    seatsForTier: seatsForTier,
    seatIsWithinCap: seatIsWithinCap,
    resolveOrgActor: resolveOrgActor,
    seatSuspensionFlags: seatSuspensionFlags,
    seatSummary: seatSummary,
    MEMBER_SEARCH_THRESHOLD: MEMBER_SEARCH_THRESHOLD,
    showMemberSearch: showMemberSearch,
    describeDeveloperChanges: describeDeveloperChanges,
    orgDisplayName: orgDisplayName,
    buildOrgTokenPayload: buildOrgTokenPayload,
    validTransition: validTransition
};
